import type {
  PurchaseOrder,
  ApiPurchaseOrderListItem,
  ApiPurchaseOrderDetails,
  ApiPODetailsItem,
  PurchaseOrderDetailsResponse,
  POOrderItem,
  POSupplierInfo,
  PODocument,
} from "@/app/types/purchaseOrders.types";

function parseNum(value: string | number | undefined): number {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  const normalized = String(value).replace(/,/g, "");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

export function mapApiPOToPO(api: ApiPurchaseOrderListItem): PurchaseOrder {
  const deliveryFrom =
    api.delivery_date_from ?? api.expected_delivery_date ?? "";
  const deliveryTo = api.delivery_date_to;
  const expectedDelivery =
    deliveryTo && deliveryFrom
      ? `${deliveryFrom} - ${deliveryTo}`
      : deliveryFrom;

  const totalPrice = api.total_price ?? api.total_amount;
  const totalAmount = parseNum(totalPrice);

  return {
    id: api.id,
    po_number: api.po_number ?? api.po_name ?? "",
    supplier_id: api.supplier_id,
    supplier_name: api.supplier_name ?? api.supplier ?? "",
    order_type:
      (api.order_type_trans as PurchaseOrder["order_type"]) ?? "daily",
    custom_order_date: api.custom_order_date,
    expected_delivery_date: expectedDelivery,
    actual_delivery_date: api.actual_delivery_date ?? null,
    total_amount: totalAmount,
    status: (api.status as PurchaseOrder["status"]) ?? "pending",
    status_trans: api.status_trans,
    created_at: api.created_at ?? "",
    created_by: api.created_by ?? "",
  };
}

export function mapApiPODetailsToDetails(
  api: ApiPurchaseOrderDetails,
): PurchaseOrderDetailsResponse {
  const expectedDelivery = api?.expected_delivery_date ?? "";
  const actualDelivery = api?.actual_delivery_date ?? "";
  const custom_date = api.custom_date;
  const supplier = api.supplier;
  const supplierId = supplier?.id ?? 0;
  const supplierName = supplier?.name ?? "";

  const mappedSupplier: POSupplierInfo | undefined = supplier
    ? {
      id: supplier.id,
      name: supplier.name,
      phone: supplier.phone ?? "",
      email: supplier.email ?? "",
      address: supplier.address ?? "",
      city: supplier.city,
      country: supplier.country,
      contract_type: supplier.contract_type ?? "",
      contract_start_date: supplier.start_date ?? undefined,
      contract_end_date: supplier.end_date ?? undefined,
      contract_media: supplier.contract_media,
      contact_persons: supplier.contact_persons?.map((person) => ({
        name: person.name,
        email: person.email,
        phone: person.phone,
        id: person.id,
        supplier_id: person.supplier_id,
      })),
    }
    : undefined;

  const items: POOrderItem[] = (api.items ?? []).map((row) =>
    mapApiPODetailsItemToPOOrderItem(row),
  );

  const oldPrice = api.old_price;
  const finalPrice = api.final_price;
  const totalPrice = parseNum(oldPrice?.total_price);
  const priceBeforeVat = parseNum(oldPrice?.price_before_vat);
  const totalDiscount = parseNum(oldPrice?.discount);
  const subtotal = parseNum(oldPrice?.subtotal);
  const deliveryFees = parseNum(oldPrice?.delivery_fees);
  const vat = parseNum(oldPrice?.vat);
  const finalTotalPrice = parseNum(finalPrice?.final_total_price);
  const finalPriceBeforeVat = parseNum(finalPrice?.final_price_before_vat);
  const finalTotalDiscount = parseNum(finalPrice?.total_discount);
  const finalSubtotal = parseNum(finalPrice?.final_subtotal);
  const finalDeliveryFees = parseNum(finalPrice?.delivery_fees);
  const finalVat = parseNum(finalPrice?.final_vat);

  const discount_object = api.discount;
  const doc_files = api.doc_files ?? null;

  const auditLogs = (api.audit_logs ?? []) as any[];

  const firstAuditLog = auditLogs[0];
  const created_at = firstAuditLog?.created_at ?? "";
  const created_by = firstAuditLog?.user_name ?? "";

  const rejectionLog = auditLogs.find((log) =>
    log.changes?.some(
      (change: any) => change.field === "status" && change.to === "rejected",
    ),
  );
  const rejected_by = rejectionLog?.user_name ?? "";

  const receivedLog = auditLogs.find((log) =>
    log.changes?.some(
      (change: any) => change.field === "status" && change.to === "completed",
    ),
  );
  const receivedShortLog = auditLogs.find((log) =>
    log.changes?.some(
      (change: any) => change.field === "status" && change.to === "completed-close-short",
    ),
  );
  const received_by = receivedLog?.user_name || receivedShortLog?.user_name || "";

  const documents: PODocument[] =
    api.doc_file != null
      ? [
        {
          id: api.doc_file.id,
          filename: api.doc_file.origin_filename,
          origin_filename: api.doc_file.origin_filename,
          file_path: api.doc_file.file_path,
        },
      ]
      : [];

  return {
    id: api.id,
    po_number: api.po_number ?? "",
    supplier_id: supplierId,
    supplier_name: supplierName,
    order_type:
      (api.order_type as PurchaseOrderDetailsResponse["order_type"]) ?? "daily",
    expected_delivery_date: expectedDelivery,
    custom_order_date: custom_date,
    total_amount: totalPrice,
    currency: "",
    status: (api.status as PurchaseOrderDetailsResponse["status"]) ?? "pending",
    status_trans: api.status_trans,
    created_at,
    created_by,
    supplier: mappedSupplier,
    internal_notes: api.internal_note,
    supplier_notes: api.supplier_instructions,
    documents,
    items,
    price_before_vat: priceBeforeVat,
    discount: totalDiscount,
    subtotal,
    delivery_fees: deliveryFees,
    vat,
    grand_total: totalPrice,
    discount_object,
    doc_files,
    reject_reason: api.reject_reason,
    rejected_by,
    actual_delivery_date: actualDelivery,
    received_by,
    finalTotalPrice,
    finalPriceBeforeVat,
    finalTotalDiscount,
    finalSubtotal,
    finalDeliveryFees,
    finalVat,
  };
}

function mapApiPODetailsItemToPOOrderItem(api: ApiPODetailsItem): POOrderItem {
  const variation = api.variation;
  const purchasePrice = parseNum(api.price ?? variation?.price ?? "-");
  const qty = api.qty ?? 0;
  const acceptedQty = api.accepted ?? 0;
  const deliveredQty = api.received ?? 0;
  const rejectedQty = api.rejected ?? 0;
  const remainingQty = api.remaining ?? 0;
  const old_price = api.old_price;
  const subtotal_after_discount = parseNum(old_price?.total_price);
  const discountValue = parseNum(api.discount?.value);
  const discountType =
    api.discount?.type === "percentage" || api.discount?.type === "value"
      ? api.discount.type
      : undefined;
  const subtotal_before_discount =
    parseNum(old_price?.subtotal)

  const final_price = api.final_price;
  const final_subtotal_after_discount = parseNum(final_price?.final_total_price);
  const final_subtotal_before_discount =
    parseNum(final_price?.final_subtotal)

  return {
    id: api.id,
    po_item_id: api.po_item_id,
    sku: api.sku ?? "",
    item_id: variation?.id ?? api.id,
    item_name: api.name ?? "",
    purchase_uom: variation?.purchase_uom ?? "",
    pack_size: variation?.pack_size ?? 0,
    purchase_price: purchasePrice,
    quantity: qty,
    quantity_uom: variation?.base_uom ?? variation?.base_uom_short,
    subtotal_after_discount: subtotal_after_discount,
    discount_type: discountType || "percentage",
    discount_value: discountValue,
    subtotal_before_discount: subtotal_before_discount,
    variation_id: variation?.id,
    accepted_qty: acceptedQty,
    delivered_qty: deliveredQty,
    rejected_qty: rejectedQty,
    remaining_qty: remainingQty,
    final_subtotal_after_discount: final_subtotal_after_discount,
    final_subtotal_before_discount: final_subtotal_before_discount,
    status: api.status,
    rejection_reason: api.reject_reason,
    rejected_media: api.rejected_media,
  };
}
